<template>
  <div class="mx-10 ma-5">
    <v-card flat class="pa-5">
      <v-layout row v-if="errors">
        <v-flex>
          <Alert @dismissed="onDismissed" :text="errors"></Alert>
        </v-flex>
      </v-layout>
      <div>
        <PageTitleWithSearch
          :title="'Stores'"
          :btnName="'Create New Store'"
          :btnColor="'#00B3FF'"
          :btnRoute="'/stores/create'"
          :btnPermission="'store.create'"
          :btnEnable="true"
          :loading="loading"
          :search="search"
          @searchText="searchChange($event)"
        />
        <DataTableWithPagination
          :headers="headers"
          :items="storeList"
          :loading="loading"
          :page="page"
          :total="total"
          @pageChange="page = $event"
        >
          <template #row="{ item }">
            <tr :key="item.id">
              <td class="text-left">{{ item.store_code }}</td>
              <td class="text-left">{{ item.store_name }}</td>
              <td class="text-left">{{ item.branch_name }}</td>
              <td class="text-left">{{ item.is_default ? "Yes" : "No" }}</td>
              <td class="text-left">{{ item.type }}</td>
              <td class="text-left">{{ item.status }}</td>
              <td class="text-center">
                <v-btn
                  v-if="hasPermission('store.edit')"
                  class="ma-1"
                  color="#FFE0B2"
                  :to="'/stores/edit/' + item.id"
                  fab
                  x-small
                  style="box-shadow: none;"
                >
                  <v-icon small color="#FF9800">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;"
                  ><v-icon small color="#FF9800">mdi-pencil</v-icon></v-btn
                >
                <v-btn
                  v-if="hasPermission('store.delete') && !item.is_default"
                  color="#FFCDD2"
                  fab
                  x-small
                  style="box-shadow: none;"
                  @click.stop="openDeleteConfirm(item.id)"
                >
                  <v-icon small color="#FF9800">mdi-delete</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  disabled
                  fab
                  x-small
                  style="box-shadow: none;">
                  <v-icon small color="#FF9800">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </DataTableWithPagination>
      </div>
      <ConfirmModal
        :text="'Do you want to drop this store?'"
        :title="'Drop Store'"
        :color="'red'"
        :confirmOpen="confirmOpen"
        @cancelConfirm="cancelConfirm"
        @confirm="confirm"
      />
    </v-card>
    <Snackbars
      :text="successMsg"
      :snackbar="snackbar"
      :color="'warning'"
      @closeSnackbar="closeSnackbar"
    />
  </div>
</template>

<script>
import PageTitleWithSearch from "@/components/shared/PageTitleWithSearch";
import { StoreService } from "@/services/store-service.js";
import ConfirmModal from "@/components/shared/ConfirmModal";
import DataTableWithPagination from "@/components/shared/DataTableWithPagination.vue";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");
import Snackbars from "@/components/shared/Snackbars";
import { checkError } from "@/config/errors.js";
import Alert from "@/components/shared/Alert.vue";

export default {
  name: "StoreListTable",
  components: {
    PageTitleWithSearch,
    ConfirmModal,
    DataTableWithPagination,
    Snackbars,
    Alert,
  },
  data() {
    return {
      loading: false,
      search: "",
      confirmOpen: false,
      snackbar: false,
      successMsg: "",
      errors: null,
      deleteId: null,
      storeList: [],
      page: 1,
      total: 0,
      headers: [
        { text: "Store Code", align: "start", value: "store_code" },
        { text: "Store Name", align: "start", value: "store_name" },
        { text: "Branch name", align: "start", value: "branch_id" },
        { text: "Is Default", align: "start", value: "is_default" },
        { text: "Type", align: "start", value: "type" },
        { text: "Status", align: "start", value: "status" },
        { text: "Actions", align: "center", value: "actions" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  created() {
    this.fetchStoreList();
  },
  watch: {
    search(newSearch) {
      this.fetchStoreList(newSearch);
    },
    page() {
      this.fetchStoreList(this.search);
    },
  },
  methods: {
    async fetchStoreList(search = "") {
      this.loading = true;
      try {
        const response = await StoreService.getStores(search, this.page);
        this.storeList = response.data?.data || [];
        this.total = response.data?.meta?.last_page || 0;
      } catch (error) {
        this.storeList = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },
    deleteStore(id) {
      this.loading = true;
      StoreService.deleteStore(id)
        .then(() => {
          this.successMsg = "Store deleted successfully.";
          this.snackbar = true;
          this.fetchStoreList();
          this.loading = false;
        })
        .catch((error) => {
          let data = error.response?.data?.error || "Unknown error";
          let errorOutput = checkError.errorFormatting(data);

          let errorsArray = [];
          if (Array.isArray(errorOutput)) {
            errorsArray = errorOutput.filter((e) => !!e && e.trim());
            if (errorsArray.length === 0) errorsArray = [data];
          } else if (errorOutput && errorOutput.trim()) {
            errorsArray = [errorOutput];
          } else {
            errorsArray = [data];
          }
          this.errors = errorsArray;
          this.loading = false;
        });
    },
    openDeleteConfirm(id) {
      this.deleteId = id;
      this.confirmOpen = true;
    },
    cancelConfirm() {
      this.confirmOpen = false;
      this.deleteId = null;
    },
    confirm() {
      this.confirmOpen = false;
      if (this.deleteId) {
        this.deleteStore(this.deleteId);
        this.deleteId = null;
      }
    },
    searchChange(event) {
      this.search = event;
    },
    closeSnackbar() {
      this.snackbar = false;
    },
    onDismissed() {
      this.errors = null;
    },
  },
};
</script>
